import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Amplify } from 'aws-amplify';
import { RumInitializerService } from '../rum/rum-initializer.service';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  SignInInput,
  AuthUser,
  deleteUser
} from 'aws-amplify/auth';
import { environment } from '../../../environments/environment';
import { LoadingService } from '../loading/loading.service';
export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  accessToken: string | null;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AmplifyAuthService {
  private _authState = signal<AuthState>({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    error: null
  });

  readonly authState = this._authState.asReadonly();

  private http = inject(HttpClient);
  private router = inject(Router);
  private loadingService = inject(LoadingService);
  private rumService = inject(RumInitializerService);

  constructor() {
    this.configureAmplify();
    this.initializeAuthState();
  }

  private async initializeAuthState(): Promise<void> {
    try {
      await this.checkAuthState();
    } catch {
      // User not authenticated, expected on first load
    }
  }

  private configureAmplify(): void {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.cognitoUserPoolId,
          userPoolClientId: environment.cognitoAppClientId,
          loginWith: {
            email: true,
          }
        }
      }
    });
  }

  async login(email: string, password: string): Promise<void> {
    this.updateAuthState({ error: null });

    try {
      // First check if user is already authenticated
      const currentAuthState = this._authState();
      if (currentAuthState.isAuthenticated) {
        // User already authenticated, proceeding to home
        this.router.navigate(['/home/mantenedores']);
        return;
      }

      const signInInput: SignInInput = {
        username: email,
        password: password
      };

      const { isSignedIn, nextStep } = await signIn(signInInput);

      if (isSignedIn) {
        await this.handleSuccessfulLogin();
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        this.router.navigate(['/newPasswordRequire']);
      } else {
        throw new Error('Unexpected sign-in step: ' + nextStep.signInStep);
      }
    } catch (error: unknown) {
      const authError = error as { name?: string; message?: string };
      this.rumService.recordError(error);

      // Handle specific case of user already authenticated
      if (authError.name === 'UserAlreadyAuthenticatedException') {
        await this.checkAuthState();
        if (this._authState().isAuthenticated) {
          this.router.navigate(['/home/mantenedores']);
          return;
        }
        // Sesión stale: limpiar estado de Amplify y reintentar login
        await signOut();
        sessionStorage.clear();
        const retryResult = await signIn({ username: email, password });
        if (retryResult.isSignedIn) {
          await this.handleSuccessfulLogin();
        }
        return;
      }

      this.handleLoginError(authError);
      throw error;
    } finally {
      // Loading visual manejado por el componente de login (isLoading)
    }
  }

  private async handleSuccessfulLogin(): Promise<void> {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      if (session.tokens?.accessToken && session.tokens?.idToken) {
        const accessTokenString = session.tokens.accessToken.toString();

        // Check user groups for authorization
        const groups = session.tokens.accessToken.payload['cognito:groups'] || [];

        if (!Array.isArray(groups) || groups.length === 0) {
          sessionStorage.clear();
          this.updateAuthState({
            error: 'No tienes autorización para iniciar sesión',
            isAuthenticated: false,
            user: null,
            accessToken: null
          });
          return;
        }

        // Update auth state
        this.updateAuthState({
          isAuthenticated: true,
          user: user,
          accessToken: accessTokenString,
          error: null
        });

        // Navigate to home
        this.router.navigate(['/home/mantenedores']);

        // RUM: identificar usuario con email y rol
        const email = session.tokens.idToken?.payload?.['email'] as string || '';
        const role = (groups as string[])[0] || 'unknown';
        this.rumService.setUserContext(user.userId, email, role);
        this.rumService.recordEvent('login_success', { userId: user.userId, role });
      }
    } catch (error) {
      this.rumService.recordError(error);
      throw error;
    }
  }

  private handleLoginError(error: { name?: string; message?: string }): void {
    let errorMessage = 'Error al iniciar sesión. Intenta nuevamente';

    switch (error.name) {
      case 'NotAuthorizedException':
        errorMessage = 'Email o contraseña incorrectos';
        break;
      case 'UserNotConfirmedException':
        errorMessage = 'Por favor confirma tu cuenta antes de iniciar sesión';
        break;
      case 'PasswordResetRequiredException':
        errorMessage = 'Debes restablecer tu contraseña';
        break;
      case 'UserNotFoundException':
        errorMessage = 'Usuario no encontrado';
        break;
      case 'TooManyRequestsException':
        errorMessage = 'Demasiados intentos. Intenta más tarde';
        break;
      case 'InvalidParameterException':
        errorMessage = 'Parámetros de login inválidos';
        break;
      case 'UserAlreadyAuthenticatedException':
        errorMessage = 'Ya tienes una sesión activa';
        break;
    }

    this.updateAuthState({ error: errorMessage });
    this.rumService.recordEvent('login_failed', { errorType: error.name || 'unknown' });
  }

  async logout(): Promise<void> {
    try {
      const userId = this._authState().user?.userId || 'unknown';
      this.rumService.recordEvent('logout', { userId });
      this.rumService.clearUserContext();
      await signOut();
      sessionStorage.clear();

      this.updateAuthState({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        error: null
      });

      this.router.navigate(['/login']);
    } catch (error) {
      this.rumService.recordError(error);
      // Force logout even if Amplify fails
      sessionStorage.clear();
      this.updateAuthState({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        error: null
      });
      this.router.navigate(['/login']);
    }
  }

  async checkAuthState(): Promise<void> {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      if (session.tokens?.accessToken && session.tokens?.idToken) {
        const accessTokenString = session.tokens.accessToken.toString();

        // Verify token is not expired
        const accessTokenExpiry = session.tokens.accessToken.payload.exp;
        const currentTime = Math.floor(Date.now() / 1000);

        if (accessTokenExpiry && accessTokenExpiry > currentTime) {
          this.updateAuthState({
            isAuthenticated: true,
            user: user,
            accessToken: accessTokenString,
            error: null
          });

          // RUM: restaurar contexto de usuario en sesiones que recargan la pagina
          const email = session.tokens.idToken?.payload?.['email'] as string || '';
          const groups = session.tokens.accessToken.payload['cognito:groups'] as string[] || [];
          this.rumService.setUserContext(user.userId, email, groups[0] || 'unknown');
          this.rumService.recordEvent('session_restored', { userId: user.userId });
        } else {
          // Token expired, clear state
          // Token expired, clearing auth state
          sessionStorage.clear();
          this.updateAuthState({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            error: null
          });
        }
      } else {
        // No valid tokens
        sessionStorage.clear();
        this.updateAuthState({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          error: null
        });
      }
    } catch (error) {
      this.rumService.recordError(error);
      // Clear everything on error
      sessionStorage.clear();
      this.updateAuthState({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        error: null
      });
    }
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
      this.rumService.recordError(error);
      return null;
    }
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      return await getCurrentUser();
    } catch {
      return null;
    }
  }

  private updateAuthState(updates: Partial<AuthState>): void {
    this._authState.update(state => ({ ...state, ...updates }));
  }

  get isAuthenticated(): boolean {
    return this._authState().isAuthenticated;
  }

  get currentUser(): AuthUser | null {
    return this._authState().user;
  }

  get currentError(): string | null {
    return this._authState().error;
  }

  /**
   * Elimina el usuario actual de Cognito
   * IMPORTANTE: El usuario debe estar autenticado para poder eliminarse
   */
  async deleteCurrentUser(): Promise<void> {
    try {
      // Verificar que hay un usuario autenticado
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('No hay usuario autenticado para eliminar');
      }

      // Eliminar usuario de Cognito
      await deleteUser();

      // Limpiar sesión local después de eliminación exitosa
      sessionStorage.clear();
      this.updateAuthState({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        error: null
      });

    } catch (error: unknown) {
      const err = error as { message?: string };
      throw new Error(`Error al eliminar usuario de Cognito: ${err.message}`);
    }
  }

  /**
   * Crea un nuevo usuario en Cognito usando Admin Actions a través del backend
   * @param email Email del usuario
   * @param password Contraseña temporal
   * @param name Nombre completo del usuario
   * @param grupo Grupo/role del usuario
   * @returns Promise con el userId de Cognito
   */
  async createUser(email: string, password: string, name: string, grupo: string): Promise<string> {
    try {
      // El AppInterceptor agrega automáticamente el token de Cognito para apiPersonaAWS
      const result = await firstValueFrom(
        this.http.post<Record<string, unknown>>(environment.apiPersonaAWS, {
          operation_type: 'create-user',
          username: email,
          new_password: password,
          name: name,
          grupo: grupo
        })
      );

      // Intentar extraer userId de diferentes campos posibles
      const nested = result['user'] as Record<string, unknown> | undefined;
      const nestedUpper = result['User'] as Record<string, unknown> | undefined;
      const userId = result['userId'] ||
                    result['Username'] ||
                    result['userSub'] ||
                    result['id'] ||
                    nested?.['userId'] ||
                    nested?.['Username'] ||
                    nested?.['userSub'] ||
                    nestedUpper?.['Username'] ||
                    nestedUpper?.['userId'] ||
                    result['sub'] ||
                    result['cognito_user_id'] ||
                    result['user_id'];

      if (!userId) {
        this.rumService.recordError(new Error('No se pudo extraer userId de la respuesta del backend'));
        throw new Error('No se pudo obtener el ID del usuario creado');
      }

      this.rumService.recordEvent('cognito_user_created', { userId: userId as string });
      return userId as string;

    } catch (error: unknown) {
      const err = error as { message?: string; name?: string; status?: number; error?: unknown };

      // Extraer mensaje de error del response body de la Lambda
      const responseBody = err.error;
      let lambdaMessage = '';

      if (typeof responseBody === 'string') {
        lambdaMessage = responseBody;
      } else if (responseBody && typeof responseBody === 'object') {
        const body = responseBody as Record<string, unknown>;
        lambdaMessage = (body['message'] || body['errorMessage'] || body['error'] || '') as string;
      }

      const errMsg = lambdaMessage || err.message || '';
      this.rumService.recordEvent('createUser_error', {
        status: err.status ?? 0,
        response: typeof responseBody === 'string' ? responseBody : JSON.stringify(responseBody ?? {}),
        message: errMsg
      });

      // Manejar errores específicos
      let errorMessage = 'Error al crear usuario en Cognito';

      if (errMsg.includes('Authorization')) {
        errorMessage = 'No tienes permisos para crear usuarios. Verifica tu rol de administrador.';
      } else if (errMsg.includes('already exists') || errMsg.includes('UsernameExistsException')) {
        errorMessage = 'Ya existe un usuario con este email';
      } else if (errMsg.includes('InvalidParameterException') || errMsg.includes('Invalid')) {
        errorMessage = `Parámetros inválidos: ${lambdaMessage || errMsg}`;
      } else if (errMsg.includes('InvalidPasswordException') || errMsg.includes('password')) {
        errorMessage = `La contraseña no cumple los requisitos: ${lambdaMessage || errMsg}`;
      } else if (err.status === 400) {
        errorMessage = `Solicitud rechazada por el servidor: ${lambdaMessage || 'Verifique los datos ingresados'}`;
      } else {
        errorMessage = `Error al crear usuario: ${errMsg}`;
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Cambia la contraseña de un usuario usando Admin Actions
   * @param userId ID del usuario en Cognito
   * @param newPassword Nueva contraseña
   * @returns Promise que resuelve cuando se cambia la contraseña
   */
  async changeUserPassword(userId: string, newPassword: string): Promise<void> {
    try {
      // El AppInterceptor agrega automáticamente el token de Cognito para apiPersonaAWS
      await firstValueFrom(
        this.http.post(environment.apiPersonaAWS, {
          operation_type: 'change-password',
          username: userId,
          new_password: newPassword
        })
      );

    } catch (error: unknown) {
      const err = error as { message?: string; name?: string; error?: { message?: string } };

      // Manejar errores específicos
      let errorMessage = 'Error al cambiar contraseña';
      const errMsg = err.error?.message || err.message || '';

      if (errMsg.includes('Authorization')) {
        errorMessage = 'No tienes permisos para cambiar contraseñas. Verifica tu rol de administrador.';
      } else if (errMsg.includes('user not found')) {
        errorMessage = 'Usuario no encontrado en Cognito';
      } else if (errMsg.includes('password')) {
        errorMessage = 'La contraseña no cumple con los requisitos de seguridad';
      } else {
        errorMessage = `Error al cambiar contraseña: ${errMsg}`;
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Genera una contraseña temporal para nuevos usuarios
   * @returns Contraseña temporal que cumple con los requisitos de Cognito
   */
  generateTemporaryPassword(): string {
    const length = 12;
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';
    const symbols = '!@#$%^&*';
    const charset = lower + upper + digits + symbols;

    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);

    // Asegurar al menos un caracter de cada tipo requerido
    const password: string[] = [
      upper[randomValues[0] % upper.length],
      lower[randomValues[1] % lower.length],
      digits[randomValues[2] % digits.length],
      symbols[randomValues[3] % symbols.length]
    ];

    // Completar con caracteres aleatorios del charset completo
    for (let i = 4; i < length; i++) {
      password.push(charset[randomValues[i] % charset.length]);
    }

    // Mezclar usando Fisher-Yates con crypto
    const shuffleValues = new Uint32Array(password.length);
    crypto.getRandomValues(shuffleValues);
    for (let i = password.length - 1; i > 0; i--) {
      const j = shuffleValues[i] % (i + 1);
      [password[i], password[j]] = [password[j], password[i]];
    }

    return password.join('');
  }
}
